import React, { useState } from 'react';
import { Filter, X, Clock, Car, Tag, Radio, User, CheckCheck, FileText, ArrowUpDown, ArrowUp, ArrowDown, Eye, Moon, Sun, Settings, Volume2 } from 'lucide-react';
import SystemClock from './SystemClock';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../AuthContext';

const TopBar = ({
  timeFilter,
  setTimeFilter,
  TIME_FILTERS,
  showTime,
  setShowTime,
  showCar,
  setShowCar,
  showChannel,
  setShowChannel,
  showPerson,
  setShowPerson,
  branding,
  isDarkMode,
  timezone,
  setTimezone,
  isMobile,
   isMultiSelectMode,
  setIsMultiSelectMode,
  setSelectedMessages,
  selectedMessages,
  toggleMultiSelectMode,
  edgeServerEndpoint,
  userRole,
  showFullTimestamps,
  setShowFullTimestamps,
  onFilterClick,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  toggleTheme,
  timeFormat = "24h",
  isVolumeOn,
  setIsVolumeOn,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions(edgeServerEndpoint);
  
  // Show settings button only if user has access_settings permission or is admin
  const canAccessSettings = user?.role === 'admin' || hasPermission('access_settings');
  
  // Helper function to format time display based on time format preference
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '';
    if (timeFormat === '24h') return timeStr; // Already in 24h format
    
    // Convert 24h format (HH:MM:SS) to 12h format (HH:MM:SS AM/PM)
    const [hours, minutes, seconds] = timeStr.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const secs = seconds || '00';
    return `${String(hour12).padStart(2, '0')}:${minutes}:${secs} ${ampm}`;
  };
  const [isViewSettingsOpen, setIsViewSettingsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isViewModalClosing, setIsViewModalClosing] = useState(false);
  const [isFilterModalClosing, setIsFilterModalClosing] = useState(false);
//  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const closeViewModal = () => {
    setIsViewModalClosing(true);
    setTimeout(() => {
      setIsViewSettingsOpen(false);
      setIsViewModalClosing(false);
    }, 300);
  };

  const closeFilterModal = () => {
    setIsFilterModalClosing(true);
    setTimeout(() => {
      setIsFilterOpen(false);
      setIsFilterModalClosing(false);
    }, 300);
  };

  //  const toggleMultiSelectMode = () => {
  //   setIsMultiSelectMode(!isMultiSelectMode);
  //   setSelectedMessages(new Set());
  // };
  const commonStyles = {
    modalBackground: isDarkMode ? 'bg-gray-900' : 'bg-white',
    borderColor: isDarkMode ? 'border-gray-700' : 'border-gray-200',
    textColor: isDarkMode ? 'text-white' : 'text-gray-800',
    secondaryTextColor: isDarkMode ? 'text-gray-400' : 'text-gray-500',
    buttonBackground: isDarkMode ? 'bg-gray-800' : 'bg-gray-100',
    buttonHoverBackground: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200',
    toggleInactive: isDarkMode ? 'bg-gray-700' : 'bg-gray-200',
    inputBackground: isDarkMode ? 'bg-gray-800' : 'bg-gray-50',
    // Static colors for better visibility in dark mode
    headerColor: isDarkMode ? '#ffffff' : '#1f2937', // White in dark, dark gray in light
    iconColor: isDarkMode ? '#3b82f6' : '#2563eb', // Blue shades for icons
    accentColor: isDarkMode ? '#60a5fa' : '#3b82f6', // Lighter blue for accents
  };


  return (
    <>
     <div className={`sticky top-0 z-20 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'} backdrop-blur-md border-b ${commonStyles.borderColor} shadow-sm transition-colors duration-300`}>
        <div className={`${isMobile ? 'px-2' : 'px-4 sm:px-6'}`}>
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              {/* Title - icon on mobile, text on desktop */}
              {isMobile ? (
                <div 
                  className={`flex items-center justify-center h-8 w-8 rounded-full ${commonStyles.buttonBackground}`}
                  title="Messages"
                >
                  <Radio 
                    className="h-4 w-4"
                    style={{ color: commonStyles.iconColor }}
                  />
                </div>
              ) : (
                <div className="flex items-center">
                  <h2 
                    className="text-xl font-bold tracking-wide transition-all duration-300"
                    style={{ color: commonStyles.headerColor }}
                  >
                    MESSAGES
                  </h2>
                  <div 
                    className="h-1 w-12 ml-2 rounded-full opacity-80 transition-all duration-300"
                    style={{ backgroundColor: commonStyles.accentColor }}
                  />
                </div>
              )}
              
              {/* Multi-select button - icon only on mobile */}
              <button
                onClick={toggleMultiSelectMode}
                className={`${isMobile ? 'h-8 w-8 p-0 flex items-center justify-center' : 'px-3 py-2'} rounded-${isMobile ? 'full' : 'md'} text-sm font-medium transition-colors ${
                  isMultiSelectMode
                    ? "bg-blue-600 text-white"
                    : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                title={isMultiSelectMode ? "Exit Select" : "Select Messages"}
              >
                <CheckCheck className={`${isMobile ? 'w-4 h-4' : 'w-4 h-4 inline mr-2'}`} />
                {!isMobile && (isMultiSelectMode ? "Exit Select" : "Select Messages")}
              </button>
            </div>

            <div className="flex items-center space-x-3">
              {/* Live Mode Toggle */}
              {isVolumeOn !== undefined && setIsVolumeOn && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${isVolumeOn ? 'bg-blue-600/20 border border-blue-500/50' : commonStyles.buttonBackground + ' border ' + commonStyles.borderColor} transition-all duration-300`}>
                  <Volume2 className={`w-4 h-4 ${isVolumeOn ? 'text-blue-500' : commonStyles.secondaryTextColor}`} />
                  <span className={`text-xs font-medium ${isVolumeOn ? 'text-blue-600 dark:text-blue-400' : commonStyles.secondaryTextColor}`}>
                    {isMobile ? '' : 'Live'}
                  </span>
                  <button
                    onClick={() => setIsVolumeOn(!isVolumeOn)}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                      isVolumeOn ? 'bg-blue-600' : commonStyles.toggleInactive
                    }`}
                    title={isVolumeOn ? 'Turn off live mode' : 'Turn on live mode'}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        isVolumeOn ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate('/report')}
                className={`relative group inline-flex items-center justify-center ${isMobile ? 'h-8 w-8' : 'h-12 w-12'} rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300 ml-3.5`}
                aria-label="Go to reports"
                title="Incident Reports"
              >
                <FileText 
                  className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4.5 w-7.5'} group-hover:scale-110 transition-all duration-300`}
                  style={{ color: commonStyles.iconColor }}
                />
              </button>
              {/* Filter button */}
              {onFilterClick && (
                <button
                  onClick={() => setIsFilterOpen(true)}
                  className={`relative group inline-flex items-center justify-center ${isMobile ? 'h-8 w-8' : 'h-12 w-12'} rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300 ml-3.5`}
                  aria-label="Filter"
                  title="Filter"
                >
                  <Filter 
                    className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5'} group-hover:scale-110 transition-all duration-300`}
                    style={{ color: commonStyles.iconColor }}
                  />
                </button>
              )}
              <button
                onClick={() => setIsViewSettingsOpen(true)}
                className={`relative group inline-flex items-center justify-center ${isMobile ? 'h-8 w-8' : 'h-12 w-12'} rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300 ml-3.5`}
                aria-label="View Settings"
                title="View Settings"
              >
                <Eye 
                  className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5'} group-hover:scale-110 transition-all duration-300`}
                  style={{ color: commonStyles.iconColor }}
                />
              </button>
              {canAccessSettings && (
                <button
                  onClick={() => navigate("/settings")}
                  className={`relative group inline-flex items-center justify-center ${isMobile ? 'h-8 w-8' : 'h-12 w-12'} rounded-full ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} transition-all duration-300 ml-3.5`}
                  aria-label="Settings"
                  title="Settings"
                >
                  <Settings 
                    className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5'} group-hover:scale-110 transition-all duration-300`}
                    style={{ color: commonStyles.iconColor }}
                  />
                </button>
              )}
              <SystemClock
                edgeServerEndpoint={edgeServerEndpoint}
                userRole={userRole}
                isDarkMode={isDarkMode}
                timeFormat={timeFormat}
                timezone={timezone}
                setTimezone={setTimezone}
              />
            </div>
          </div>
        </div>
      </div>

      {/* View Settings Modal */}
      {isViewSettingsOpen && (
        <div 
          className={`fixed inset-0 ${isDarkMode ? 'bg-gray-900/80' : 'bg-gray-800/30'} backdrop-blur-md z-30 flex items-center justify-center transitionuffed transition-opacity duration-300 ${isViewModalClosing ? 'opacity-0' : 'opacity-100'}`}
        >
          <div 
            className={`${commonStyles.modalBackground} rounded-2xl w-full max-w-[90%] md:max-w-md mx-4 shadow-2xl relative border ${commonStyles.borderColor} overflow-hidden transition-transform duration-300 ${isViewModalClosing ? 'scale-95' : 'scale-100'}`}
          >
            <div 
              className="absolute top-0 left-0 w-full h-1 transition-colors duration-300"
              style={{ backgroundColor: commonStyles.accentColor }}
            />

            <div className={`relative flex items-center justify-between p-3 md:p-4 border-b ${commonStyles.borderColor}`}>
              <h3 className={`text-base md:text-lg font-bold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                VIEW SETTINGS
              </h3>
              <button
                onClick={closeViewModal}
                className={`p-1 md:p-1.5 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors duration-200`}
                aria-label="Close view settings"
              >
                <X className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} />
              </button>
            </div>

            <div className="relative p-4 md:p-6 space-y-4 md:space-y-6">

              <div className="space-y-2 md:space-y-3">
                {[
                  { label: 'TIMING', icon: Clock, state: showTime, setState: setShowTime },
                  { label: 'TAG', icon: Tag, state: showCar, setState: setShowCar },
                  { label: 'CHANNEL', icon: Radio, state: showChannel, setState: setShowChannel },
                  { label: 'PERSON', icon: User, state: showPerson, setState: setShowPerson },
                ].map(({ label, icon: Icon, state, setState }) => (
                  <div 
                    key={label} 
                    className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${state ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                    style={{ 
                      borderColor: state ? commonStyles.accentColor : commonStyles.borderColor,
                      backgroundColor: state ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                    }}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`p-1 md:p-1.5 rounded-lg ${state ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                        style={{ backgroundColor: state ? commonStyles.accentColor + '30' : '' }}
                      >
                        <Icon 
                          className="h-3.5 w-3.5 transition-all duration-300"
                          style={{ color: commonStyles.accentColor }}
                        />
                      </div>
                      <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                        {label}
                      </span>
                    </div>
                    <button
                      onClick={() => setState(!state)}
                      className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                        state 
                          ? 'bg-blue-600' 
                          : isDarkMode 
                            ? 'bg-gray-600' 
                            : 'bg-gray-300'
                      }`}
                      aria-checked={state}
                      role="switch"
                    >
                      <span 
                        className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                          state 
                            ? 'translate-x-4 md:translate-x-4 bg-white' 
                            : isDarkMode 
                              ? 'translate-x-0.5 bg-gray-400' 
                              : 'translate-x-0.5 bg-gray-500'
                        }`} 
                      />
                    </button>
                  </div>
                ))}
              </div>

              {/* Show Full Timestamps Toggle */}
              <div className="space-y-2">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  DATE
                </label>
                <div 
                  className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${showFullTimestamps ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                  style={{ 
                    borderColor: showFullTimestamps ? commonStyles.accentColor : commonStyles.borderColor,
                    backgroundColor: showFullTimestamps ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                  }}
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className={`p-1 md:p-1.5 rounded-lg ${showFullTimestamps ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                      style={{ backgroundColor: showFullTimestamps ? commonStyles.accentColor + '30' : '' }}
                    >
                      <Clock 
                        className="h-3.5 w-3.5 transition-all duration-300"
                        style={{ color: commonStyles.accentColor }}
                      />
                    </div>
                    <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                      Show full timestamps
                    </span>
                  </div>
                  <button
                    onClick={() => setShowFullTimestamps(!showFullTimestamps)}
                    className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                      showFullTimestamps 
                        ? 'bg-blue-600' 
                        : isDarkMode 
                          ? 'bg-gray-600' 
                          : 'bg-gray-300'
                    }`}
                    aria-checked={showFullTimestamps}
                    role="switch"
                  >
                    <span 
                      className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                        showFullTimestamps 
                          ? 'translate-x-4 md:translate-x-4 bg-white' 
                          : isDarkMode 
                            ? 'translate-x-0.5 bg-gray-400' 
                            : 'translate-x-0.5 bg-gray-500'
                      }`} 
                    />
                  </button>
                </div>
              </div>

              {/* Theme Toggle */}
              {toggleTheme && (
                <div className="space-y-2">
                  <label 
                    className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300`}
                    style={{ color: commonStyles.accentColor }}
                  >
                    {isDarkMode ? (
                      <Sun className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                    ) : (
                      <Moon className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                    )}
                    THEME
                  </label>
                  <div 
                    className={`flex items-center justify-between p-2 md:p-3 rounded-lg border ${isDarkMode ? 'border-opacity-50' : 'border-opacity-20'} transition-all duration-300`}
                    style={{ 
                      borderColor: isDarkMode ? commonStyles.accentColor : commonStyles.borderColor,
                      backgroundColor: isDarkMode ? commonStyles.accentColor + '10' : (isDarkMode ? 'rgb(31 41 55)' : 'rgb(249 250 251)')
                    }}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`p-1 md:p-1.5 rounded-lg ${isDarkMode ? '' : commonStyles.buttonBackground} transition-all duration-300`}
                        style={{ backgroundColor: isDarkMode ? commonStyles.accentColor + '30' : '' }}
                      >
                        {isDarkMode ? (
                          <Sun 
                            className="h-3.5 w-3.5 transition-all duration-300"
                            style={{ color: commonStyles.accentColor }}
                          />
                        ) : (
                          <Moon 
                            className="h-3.5 w-3.5 transition-all duration-300"
                            style={{ color: commonStyles.accentColor }}
                          />
                        )}
                      </div>
                      <span className={`text-xs font-semibold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                        {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                      </span>
                    </div>
                    <button
                      onClick={toggleTheme}
                      className={`relative inline-flex h-4 md:h-5 w-8 md:w-9 items-center rounded-full transition-all duration-300 ease-out focus:outline-none ${
                        isDarkMode 
                          ? 'bg-blue-600' 
                          : 'bg-gray-300'
                      }`}
                      aria-checked={isDarkMode}
                      role="switch"
                    >
                      <span 
                        className={`inline-block h-3 md:h-4 w-3 md:w-4 transform rounded-full shadow-md transition-all duration-300 ease-out ${
                          isDarkMode 
                            ? 'translate-x-4 md:translate-x-4 bg-white' 
                            : 'translate-x-0.5 bg-gray-500'
                        }`} 
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={`relative flex justify-end px-4 md:px-6 py-3 md:py-4 border-t ${commonStyles.borderColor} transition-colors duration-300`}>
              <button
                onClick={closeViewModal}
                className={`px-4 md:px-5 py-1.5 md:py-2 rounded-lg text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95`}
                style={{ backgroundColor: commonStyles.accentColor, outline: 'none' }}
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterOpen && (
        <div 
          className={`fixed inset-0 ${isDarkMode ? 'bg-gray-900/80' : 'bg-gray-800/30'} backdrop-blur-md z-30 flex items-center justify-center transitionuffed transition-opacity duration-300 ${isFilterModalClosing ? 'opacity-0' : 'opacity-100'}`}
        >
          <div 
            className={`${commonStyles.modalBackground} rounded-2xl w-full max-w-[90%] md:max-w-md mx-4 shadow-2xl relative border ${commonStyles.borderColor} overflow-hidden transition-transform duration-300 ${isFilterModalClosing ? 'scale-95' : 'scale-100'}`}
          >
            <div 
              className="absolute top-0 left-0 w-full h-1 transition-colors duration-300"
              style={{ backgroundColor: commonStyles.accentColor }}
            />

            <div className={`relative flex items-center justify-between p-3 md:p-4 border-b ${commonStyles.borderColor}`}>
              <h3 className={`text-base md:text-lg font-bold tracking-wide ${commonStyles.textColor} transition-colors duration-300`}>
                FILTER
              </h3>
              <button
                onClick={closeFilterModal}
                className={`p-1 md:p-1.5 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors duration-200`}
                aria-label="Close filter"
              >
                <X className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} />
              </button>
            </div>

            <div className="relative p-4 md:p-6 space-y-5 md:space-y-6">
              {/* TIME RANGE */}
              <div className="space-y-2.5">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300 mb-2`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  TIME RANGE
                </label>
                <div className={`relative rounded-lg border ${commonStyles.borderColor} transition-colors duration-300`}>
                  <select
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className={`w-full appearance-none rounded-lg ${commonStyles.inputBackground} px-3 py-2.5 text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                    style={{ borderColor: commonStyles.accentColor + '40' }}
                  >
                    {Object.values(TIME_FILTERS).map((filter) => (
                      <option key={filter} value={filter} className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                        {filter}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <svg className={`h-4 w-4 ${commonStyles.secondaryTextColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Custom Date Range Filter */}
              <div className="space-y-2.5">
                <label 
                  className={`flex items-center gap-2 text-xs font-semibold tracking-wide transition-colors duration-300 mb-2`}
                  style={{ color: commonStyles.accentColor }}
                >
                  <Clock className="h-3.5 w-3.5" style={{ color: commonStyles.accentColor }} />
                  CUSTOM DATE RANGE
                </label>
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => setStartDate(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate || undefined}
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={startTime || ''}
                        onChange={(e) => setStartTime(e.target.value)}
                        step="1"
                        lang="en-GB"
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                        // HTML5 time inputs always use 24-hour format internally (HH:MM)
                        // lang="en-GB" helps ensure 24-hour display format in most browsers
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={endTime || ''}
                        onChange={(e) => setEndTime(e.target.value)}
                        step="1"
                        lang="en-GB"
                        className={`w-full px-3 py-2.5 rounded-lg border ${commonStyles.borderColor} ${commonStyles.inputBackground} text-sm ${commonStyles.textColor} focus:outline-none transition-colors duration-300`}
                        style={{ borderColor: commonStyles.accentColor + '40' }}
                        // HTML5 time inputs always use 24-hour format internally (HH:MM)
                        // lang="en-GB" helps ensure 24-hour display format in most browsers
                      />
                    </div>
                  </div>
                </div>
                {(startDate || endDate) && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {startDate && endDate 
                        ? `${startDate}${startTime ? ' ' + formatTimeDisplay(startTime) : ''} to ${endDate}${endTime ? ' ' + formatTimeDisplay(endTime) : ''}`
                        : startDate 
                        ? `Start: ${startDate}${startTime ? ' ' + formatTimeDisplay(startTime) : ''}`
                        : `End: ${endDate}${endTime ? ' ' + formatTimeDisplay(endTime) : ''}`
                      }
                    </span>
                    <button
                      onClick={() => {
                        setStartDate('');
                        setEndDate('');
                        setStartTime('');
                        setEndTime('');
                      }}
                      className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={`relative flex justify-end px-4 md:px-6 py-3 md:py-4 border-t ${commonStyles.borderColor} transition-colors duration-300`}>
              <button
                onClick={closeFilterModal}
                className={`px-4 md:px-5 py-1.5 md:py-2 rounded-lg text-sm font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg active:scale-95`}
                style={{ backgroundColor: commonStyles.accentColor, outline: 'none' }}
              >
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;